import React, { Component } from 'react'
import { browserHistory } from 'react-router'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import Dropzone from 'react-dropzone'
import { blobToBase64 } from '../../../../utils/blob'
import Icon from '../../../util/Icon'
import api from '../../../../api'
import _ from 'lodash'

/*
 * The released version of RichTextEditor has a bug, see
 *
 *  https://github.com/sstur/react-rte/issues/374
 *
 * In order to avoid the error message, we're using
 * a patched version. In package.json,
 *
 *  "react-rte": "^0.16.3",
 *
 * has been replaced with
 *
 *  "react-rte": "https://github.com/machineghost/react-rte/",
 *
 * Go back to the main repo once this issue has been resolved.
 */
import RichTextEditor from 'react-rte'

import Form from '../../../util/Form'

import MlsStore from '../../../../stores/mls'
import MlsAutocomplete from '../../../util/auto_complete/MlsAutocomplete'
import RealtorAssociations from '../../../../stores/realtor_associations'
import RaAutocomplete from '../../../util/auto_complete/RaAutocomplete'

const MlsAdminUIStore = observable({
  selection: null,
  section: 'basic',
  compliance_preview: {},
  compliance_preview_loading: false,
  update_compliance(id) {
    this.compliance_preview_loading =  true
    api.get('mls/preview/' + id).then(res => {
      this.compliance_preview = res.data
      this.compliance_preview_loading = false
    })
  }
})

@observer
export default class Mls extends Component {
  state = {
    summary_blurb: RichTextEditor.createEmptyValue(),
    tools_blurb: RichTextEditor.createEmptyValue(),
    long_blurb: RichTextEditor.createEmptyValue(),
    mls_fee_annotation: RichTextEditor.createEmptyValue()
  }

  static propTypes = {
    mls_id: PropTypes.number
  }

  static defaultProps = {
    mls_id: null
  }

  constructor(props) {
    super(props)

    // proper place to instantiate a store?
    this.mlsStore = new MlsStore()
    this.realtorAssociations = new RealtorAssociations()
    this.MlsAdminUIStore = MlsAdminUIStore
  }

  componentDidMount() {
    this.mlsStore.loadMlsInfo()
    this.realtorAssociations.load()
    this.loadMls(this.props.params.mls_id)
  }

  /*
   * Load the MLS given its id and preprocess it
   */
  loadMls(mls_id) {
    if (mls_id) {
      this.mlsStore.load(mls_id).then(() => {
        this.MlsAdminUIStore.selection = this.mlsStore.findById(mls_id)
        this.previewCompliance()
        this.loadHTMLFields()
      })
    } else {
      this.MlsAdminUIStore.selection = null
    }
  }

  /*
   * Select a new MLS.
   *
   * Clears the drop zone.
   */
  handleSelectMls = (mls) => {
    this.setState({ file: null })

    if (typeof(mls) === 'undefined') {
      this.loadMls(null)
      browserHistory.push('/admin/mls')
    } else {
      this.loadMls(mls.id)
      browserHistory.push('/admin/mls/' + mls.id)
    }
  }

  /*
   * Select which section of the form to display
   */
  handleSelectSection = (e) => {
    this.MlsAdminUIStore.section = e.target.value
  }

  previewCompliance = () => {
    this.MlsAdminUIStore.compliance_preview_loading =  true
    this.MlsAdminUIStore.update_compliance(this.MlsAdminUIStore.selection.id)
  }

  /*
   * An MLS has been loaded, initialize the fields that use HTML
   */
  loadHTMLFields = () => {
    const mls = this.MlsAdminUIStore.selection

    this.setState({
      'summary_blurb': RichTextEditor.createValueFromString(mls.mls_coverage.summary_blurb_html, 'html'),
      'tools_blurb': RichTextEditor.createValueFromString(mls.mls_coverage.tools_blurb_html, 'html'),
      'long_blurb': RichTextEditor.createValueFromString(mls.mls_coverage.long_blurb_html, 'html'),
      'mls_fee_annotation': RichTextEditor.createValueFromString(mls.mls_coverage.mls_fee_annotation, 'html')
    })
  }

  /*
   * Handle a 'submit' on the form. This used to trigger
   * previewCompliance() after a timeout, but it was unreliable.
   * We now handle it in the promise returned by update().
   */
  handleData = data => {
    this.MlsAdminUIStore.compliance_preview_loading = true
    data.id = this.MlsAdminUIStore.selection.id
    return this.mlsStore.update(data).then(this.previewCompliance)
  }

  /*
   * Process a change on a field, using the event target name to
   * recognize fields that belong to the mls_compliance or
   * mls_coverage subobjects. If neither, it's a field of the mls
   * object itself.
   * We now also handle sub-subobjects.
   */
  handleMlsFieldChange = e => {
    let mls = this.MlsAdminUIStore.selection

    let targetObj = mls
    let targetObjProp = e.target.name

    let m = e.target.name.match(/mls_coverage\.(.*)/)
    if (m) {
      targetObj = mls.mls_coverage
      targetObjProp = m[1]
    }

    m = e.target.name.match(/mls_compliance\.disclaimers\.(.*)/)
    if (m) {
      targetObj = mls.mls_compliance.disclaimers
      targetObjProp = m[1]
    } else {
      m = e.target.name.match(/mls_compliance\.(.*)/)
      if (m) {
        targetObj = mls.mls_compliance
        targetObjProp = m[1]
      }
    }

    switch (e.target.type) {
      case 'radio':
        targetObj[targetObjProp] = e.target.checked ? e.target.value : false
        break
      case 'checkbox':
        targetObj[targetObjProp] = e.target.checked ? e.target.value : false
        break
      default:
        targetObj[targetObjProp] = e.target.value
    }
  }

  /*
   * Handle a change on a RichTextEditor, storing the value in the local
   * state for later conversion to an HTML string.
   */
  handleMlsFieldChangeRTE = (which, value) => {
    this.setState({[which]: value})
  }

  /*
   * Post-process the data object after it's been serialized, to add
   * in the properties that are not handled using standard form fields.
   *
   * We also need to handle the booleans here, the serializer tries to
   * render false as "".
   *
   * We also handle the RichTextEditors here, since the documentation
   * advises against converting the value to a string on every keypress.
   *
   * This is now asynchronous, because we potentially have to fetch the
   * logo file if one was dropped on the dropzone.
   */
  postProcessData = data => {
    const mls = this.MlsAdminUIStore.selection
    const { file, summary_blurb, tools_blurb, long_blurb, mls_fee_annotation } = this.state // eslint-disable-line no-unused-vars

    data['mls_coverage.administrative_area_level_1'] = mls.mls_coverage.administrative_area_level_1 ?
      mls.mls_coverage.administrative_area_level_1.toJS() : []

    data['mls_coverage.top_cities'] = mls.mls_coverage.top_cities ?
      mls.mls_coverage.top_cities.toJS() : []

    data['mls_coverage.mls_realtor_associations'] = mls.mls_coverage.realtor_associations.toJS().map(x => ({realtor_association_id: x.id}))

    // Booleans
    data['active'] = !!data['active']
    data['mls_compliance.list_view_listing_preview_sqft_show_source'] = !!data['mls_compliance.list_view_listing_preview_sqft_show_source']
    data['mls_compliance.listtrac'] = !!data['mls_compliance.listtrac']
    data['mls_compliance.price_per_sq_ft'] = !!data['mls_compliance.price_per_sq_ft']
    data['mls_compliance.sold_data'] = !!data['mls_compliance.sold_data']
    data['mls_compliance.hide_sold_price'] = !!data['mls_compliance.hide_sold_price']
    data['mls_compliance.hide_sold_date'] = !!data['mls_compliance.hide_sold_date']
    data['mls_compliance.sold_images'] = !!data['mls_compliance.sold_images']

    // RichTextEditors
    /* temporarily disabled to support entering video links
    data['mls_coverage.summary_blurb_html'] = summary_blurb.toString('html')
    */
    data['mls_coverage.tools_blurb_html'] = tools_blurb.toString('html')
    data['mls_coverage.long_blurb_html'] = long_blurb.toString('html')
    data['mls_coverage.mls_fee_annotation'] = mls_fee_annotation.toString('html')


    return new Promise(resolve => {
      if (file) {
        blobToBase64(file).then(b64 => {
          data['mls_compliance.logo'] = b64
          resolve(data)
        })
      } else {
        resolve(data)
      }
    })
  }

  /*
   * Add an administrative_area_level_1 to the list, using the
   * value in the input field
   */
  addAdminArea = () => {
    const el = document.getElementById('admin_area')
    const aa = el.value.trim()

    if (!aa) return

    if (!this.MlsAdminUIStore.selection.mls_coverage.administrative_area_level_1)
      this.MlsAdminUIStore.selection.mls_coverage.administrative_area_level_1 = []

    let aaSelected = this.MlsAdminUIStore.selection.mls_coverage.administrative_area_level_1

    const inSel = aaSelected.toJS().find(x => x == aa)
    if (inSel) return

    aaSelected.push(aa)
  }

  /*
   * Remove an administrative_area_level_1 from the list
   */
  removeAdminArea = aa => {
    let aaSelected = this.MlsAdminUIStore.selection.mls_coverage.administrative_area_level_1.toJS()

    _.remove(aaSelected, x => x == aa)

    this.MlsAdminUIStore.selection.mls_coverage.administrative_area_level_1 = aaSelected
  }

  /*
   * Add a top_cities item to the list, using the
   * value in the input field
   */
  addTopCity = () => {
    const el = document.getElementById('top_city')
    const tc = el.value.trim()

    if (!tc) return

    if (!this.MlsAdminUIStore.selection.mls_coverage.top_cities)
      this.MlsAdminUIStore.selection.mls_coverage.top_cities = []

    let tcSelected = this.MlsAdminUIStore.selection.mls_coverage.top_cities

    const inSel = tcSelected.toJS().find(x => x == tc)
    if (inSel) return

    tcSelected.push(tc)
  }

  /*
   * Remove a top_cities item from the list
   */
  removeTopCity = tc => {
    let tcSelected = this.MlsAdminUIStore.selection.mls_coverage.top_cities.toJS()

    _.remove(tcSelected, x => x == tc)

    this.MlsAdminUIStore.selection.mls_coverage.top_cities = tcSelected
  }

  /*
   * Add a realtor association to the list, using the provided item.
   * We maintain the list in sorted order.
   */
  addRealtorAssoc = (selected) => {
    const id = selected.id
    let raSelected = this.MlsAdminUIStore.selection.mls_coverage.realtor_associations

    const inSel = raSelected.toJS().find(x => x.id == id)
    if (inSel) return

    raSelected.push(selected)
    this.MlsAdminUIStore.selection.mls_coverage.realtor_associations.replace(raSelected.sort((a,b) => a.name.localeCompare(b.name)))
  }

  /*
   * Remove a realtor association from the list given its id
   */
  removeRealtorAssoc = id => {
    let raSelected = this.MlsAdminUIStore.selection.mls_coverage.realtor_associations.toJS()

    _.remove(raSelected, x => x.id === id)

    this.MlsAdminUIStore.selection.mls_coverage.realtor_associations = raSelected
  }

  /*
   * Respond to a drop on the logo upload zone
   */
  handleFileDrop = files => {
    if (!files[0]) {
      alert('Invalid File! Please make sure you are uploading a proper image file no more than 512KB.')
    }
    this.setState({ file: files[0] })
  }

  /*
   * Show the logo dropzone or preview image
   */
  getFilePreview(className) {
    const { file } = this.state
    let inner
    if (file) {
      inner = <div className="preview" style={{ backgroundImage: `url(${file.preview})` }} />
    } else {
      inner = <div className="status"/>
    }
    return (
      <div className={className}>{inner}</div>
    )
  }

  render() {
    const mls = this.MlsAdminUIStore.selection
    const section = this.MlsAdminUIStore.section
    const disclaimers_text = mls && mls.mls_compliance && mls.mls_compliance.disclaimers_text || {}
    const disclaimers = mls && mls.mls_compliance && mls.mls_compliance.disclaimers || {}
    const complianceBackEndVariables = {
      'last_incremented_at': 'Last update of mls data',
      'this_year': 'The current year; useful for Copyright text',
      'office_names': 'List of all office names from the current website',
      'logo_url': 'A URL to the logo for the mls',
      'logo': 'An <img> tag with  the logo for the mls',
      'courtesy_of_template': 'The courtesy_of_template so it can be re-used easily in other disclaimers',
      'selling_agent_template': 'The "bought with" template so it can be re-used easily in other disclaimers',
      'broker_compliance_blurb': 'Broker (aka MlsPermission) specific compliance text that an individual customer might want included. This is auto-appended to the list and detail views unless you place it explicitly.',
      'equal_housing_opportunity_logo': 'An <img> tag showing the Equal Housing Opportunity logo'

    }
    const complianceFrontEndVariables = {
      '__LISTING_OFFICE__'                  : 'The office for a specific listing',
      '__LISTING_OFFICE_STATE_LICENSE__'    : 'The state license number for the principal broker of the office',
      '__LISTING_OFFICE_PHONE_NUMBER__'     : 'The public phone number to contact the listing office',
      '__LISTING_OFFICE_EMAIL__'            : 'The email address to contact the listing office',
      '__LISTING_ORIG_MLS_NAME__'           : 'The original MLS from which this listing was input.',
      '__LISTING_AGENT__'                   : 'The agent for a specific listing',
      '__LISTING_AGENT_STATE_LICENSE__'     : 'The state license number for the listing agent',
      '__LISTING_AGENT_PHONE_NUMBER__'      : 'The public phone number to contact the listing agent',
      '__LISTING_IDX_CONTACT_EMAIL__'       : 'The public email attached to a listing; can be office, agent, or special',
      '__LISTING_IDX_CONTACT_PHONE__'       : 'The public phone number attached to a listing; can be office, agent, or special',
      '__LISTING_IDX_CONTACT_PREFER_EMAIL__': 'Prefer LISTING_IDX_CONTACT_EMAIL, fallback to LISTING_IDX_CONTACT_PHONE',
      '__LISTING_IDX_CONTACT_PREFER_PHONE__': 'Prefer LISTING_IDX_CONTACT_PHONE, fallback to LISTING_IDX_CONTACT_EMAIL',
      '__SELLING_AGENT__'                   : 'The selling agent for a specific listing',
      '__SELLING_AGENT_PHONE_NUMBER__'      : 'The public phone number to contact the listing selling agent',
      '__SELLING_OFFICE__'                  : 'The selling office for a specific listing',
      '__SELLING_OFFICE_PHONE_NUMBER__'     : 'The public phone number to contact the listing selling office',
      '__BROKER_ATTRIBUTION__'             : 'Broker Attribution Contact',
    }

    return (
      <div className="container padded mls-admin">
        <div className="container">
          <div className="panel full padded">
            <div className="subpanel">
              <div className="group">
                <label className="left-label">Select an MLS: </label>
                <MlsAutocomplete mlsStore={this.mlsStore} onSelect={this.handleSelectMls} />
              </div>
              {mls && (
                <span>
                  <label>Section: </label>
                  <select name="section" value={section} onChange={this.handleSelectSection}>
                    <option value="basic">Basic Information</option>
                    <option value="coverage">Coverage</option>
                    <option value="compliance">Compliance</option>
                  </select>
                </span>
              )}
            </div>
          </div>
        </div>
        {mls && (
          <Form ref={el => this.form = el && el.form} className="full" onData={this.handleData} errorsAbove={true} includeEmpty={true} postProcessData={this.postProcessData}>
            <div className="panel full padded">
              <div className="subpanel">
                <span className="left-title">Editing: {mls.name} [id#{mls.id}] [ouid:{mls.ouid}]</span>
                <br/>
                <a href={'http://retsync-new.showcaseidx.com/mls/ouid/' + mls.ouid} target='_blank'>Retsync Admin For
                  This MLS</a>
                <button type="submit" className="update-button big shadowed">Update
                  MLS {this.MlsAdminUIStore.compliance_preview_loading && (<span>[loading...]</span>)}</button>
              </div>

              <div className={section === 'compliance' ? 'inherit' : 'hidden'}>
                <div className="form-section">
                  <input id="mls_compliance.sold_data" name="mls_compliance.sold_data" type="checkbox"
                         checked={mls.mls_compliance.sold_data || false} value="true"
                         onChange={this.handleMlsFieldChange}/>
                  <label htmlFor="mls_compliance.sold_data">Enable Sold Data for this MLS?</label>
                </div>
                <div className="form-section">
                  <input id="mls_compliance.hide_sold_price" name="mls_compliance.hide_sold_price" type="checkbox"
                         checked={mls.mls_compliance.hide_sold_price || false} value="true"
                         onChange={this.handleMlsFieldChange}/>
                  <label htmlFor="mls_compliance.hide_sold_price">Do Not Display Sold Price</label>
                </div>
                <div className="form-section">
                  <input id="mls_compliance.hide_sold_date" name="mls_compliance.hide_sold_date" type="checkbox"
                         checked={mls.mls_compliance.hide_sold_date || false} value="true"
                         onChange={this.handleMlsFieldChange}/>
                  <label htmlFor="mls_compliance.hide_sold_date">Do Not Display Sold Date</label>
                </div>
                <div className="form-section">
                  <input id="mls_compliance.sold_images" name="mls_compliance.sold_images" type="checkbox"
                         checked={mls.mls_compliance.sold_images || false} value="true"
                         onChange={this.handleMlsFieldChange}/>
                  <label htmlFor="mls_compliance.sold_images">Allow All Images on Sold Data</label>
                </div>
              </div>

              <div className="subpanel">
                <span
                    title="(based on presence of `-i_` in index_name)">IRE Feed? {mls.index_name && mls.index_name.match(/-i_/) ? 'YES - for eXp portal use only!' : 'NO'}</span>
              </div>

            </div>
            <div className={section === 'basic' ? 'container' : 'hidden'}>
              <div className="panel full padded">
                <h3>Basic Information</h3>

                <div className="form-section">
                <label>MLS Name</label>
                  <input name="name" value={mls.name || ""} onChange={this.handleMlsFieldChange}
                         placeholder="Enter the MLS Name"/>
                </div>

                <div className="form-section">
                  <label>MLS Local Time Zone</label><br />
                  <select name="time_zone" value={mls.time_zone || ""} onChange={this.handleMlsFieldChange}>
                    <option name="UTC">UTC</option>
                    <option name="America/Adak">America/Adak</option>
                    <option name="America/Anchorage">America/Anchorage</option>
                    <option name="America/Boise">America/Boise</option>
                    <option name="America/Chicago">America/Chicago</option>
                    <option name="America/Denver">America/Denver</option>
                    <option name="America/Detroit">America/Detroit</option>
                    <option name="America/Indiana/Indianapolis">America/Indiana/Indianapolis</option>
                    <option name="America/Indiana/Knox">America/Indiana/Knox</option>
                    <option name="America/Indiana/Marengo">America/Indiana/Marengo</option>
                    <option name="America/Indiana/Petersburg">America/Indiana/Petersburg</option>
                    <option name="America/Indiana/Tell_City">America/Indiana/Tell_City</option>
                    <option name="America/Indiana/Vevay">America/Indiana/Vevay</option>
                    <option name="America/Indiana/Vincennes">America/Indiana/Vincennes</option>
                    <option name="America/Indiana/Winamac">America/Indiana/Winamac</option>
                    <option name="America/Juneau">America/Juneau</option>
                    <option name="America/Kentucky/Louisville">America/Kentucky/Louisville</option>
                    <option name="America/Kentucky/Monticello">America/Kentucky/Monticello</option>
                    <option name="America/Los_Angeles">America/Los_Angeles</option>
                    <option name="America/Menominee">America/Menominee</option>
                    <option name="America/Metlakatla">America/Metlakatla</option>
                    <option name="America/New_York">America/New_York</option>
                    <option name="America/Nome">America/Nome</option>
                    <option name="America/North_Dakota/Beulah">America/North_Dakota/Beulah</option>
                    <option name="America/North_Dakota/Center">America/North_Dakota/Center</option>
                    <option name="America/North_Dakota/New_Salem">America/North_Dakota/New_Salem</option>
                    <option name="America/Phoenix">America/Phoenix</option>
                    <option name="America/Sitka">America/Sitka</option>
                    <option name="America/Yakutat">America/Yakutat</option>
                    <option name="Pacific/Honolulu">Pacific/Honolulu</option>
                    <option name="Canada/Atlantic">Canada/Atlantic</option>
                    <option name="Canada/Central">Canada/Central</option>
                    <option name="Canada/Eastern">Canada/Eastern</option>
                    <option name="Canada/Mountain">Canada/Mountain</option>
                    <option name="Canada/Newfoundland">Canada/Newfoundland</option>
                    <option name="Canada/Pacific">Canada/Pacific</option>
                    <option name="Canada/Saskatchewan">Canada/Saskatchewan</option>
                    <option name="Canada/Yukon">Canada/Yukon</option>
                  </select>
                </div>

                <div className="form-section">
                  <input id="mls_active" name="active" type="checkbox" checked={mls.active || false} value="true" onChange={this.handleMlsFieldChange} />
                  <label htmlFor="mls_active">Active?</label>
                </div>

                <div className="form-section">
                  <label>Availability</label><br />
                  <select name="availability" value={mls.availability || ""} onChange={this.handleMlsFieldChange}>
                    <option value="unavailable">Unavailable (not available at this time)</option>
                    <option value="coming_soon">Coming Soon (live within 30 days)</option>
                    <option value="ga">GA (generally available / public)</option>
                  </select>
                </div>

                <div className="form-section">
                  <label>MLS Fee</label>
                  <input name="fee" value={mls.fee || ""} onChange={this.handleMlsFieldChange} placeholder="Enter the MLS Fee" />
                  <br />

                  <input id="mls_per_website" name="per" type="radio" value="website" checked={mls.per == 'website' ? 'checked' : ''} onChange={this.handleMlsFieldChange} />
                  <label htmlFor="mls_per_website">Fee is per-month, per-website (most common)</label>
                  <br />

                  <input id="mls_per_account" name="per" type="radio" value="team" checked={mls.per == 'team' ? 'checked' : ''} onChange={this.handleMlsFieldChange} />
                  <label htmlFor="mls_per_account">Fee is per-month, per-account</label>
                </div>

                <div className="form-section">
                  <label>MLS ID</label>
                  <p>This is the &quot;acronym&quot; for the MLS. WARNING: this is used internally in the database and if changed will break saved listings, etc. An email will be sent to help@showcaseidx.com with a cleanup script to run to update the old name to the new one across our system. Also, note that the <i>legacy_mls_id</i> will be used to redirect existing URLs to the prior mls_id, so be sure it's properly filled out when changing the mls_id.</p>
                  <div>
                    <input name="mls_id" value={mls.mls_id || ""} onChange={this.handleMlsFieldChange} placeholder="Enter the MLS ID" style={{display: 'inline-block', width:'50%'}} />
                    <input name="legacy_mls_id" value={mls.legacy_mls_id || ""} onChange={this.handleMlsFieldChange} placeholder="Map legacy mls_id here (useful for renames)" style={{display:'inline-block',width:'50%'}} />
                </div>
                </div>

                {false && <div className="form-section">
                  <label>ES Index Name</label>
                  <p>This is internal index name in our ElasticSearch cluster. Only devs should edit this field.</p>
                  <input name="index_name" value={mls.index_name || ""} onChange={this.handleMlsFieldChange} placeholder="Enter the ES Index Name" />
                </div>}

                <div className="form-section">
                  <label>Cirrus OSN (Origniating System Name):CentralPanhandle</label>
                  <p>This is internal OSN name in our cirrus. Only devs should edit this field.</p>
                  <input name="cirrus_mls_id" value={mls.cirrus_mls_id || ""} onChange={this.handleMlsFieldChange} placeholder="Enter the cirrus OSN Name" />
                </div>

                <div className="form-section">
                  <label>Dropbox Sign Template ID Agent/Broker</label>
                  <input name="hellosign_id" value={mls.hellosign_id || ""} onChange={this.handleMlsFieldChange} />
                </div>

                <div className="form-section">
                  <label>Dropbox Sign Template ID Broker Only</label>
                  <input name="hellosign_template_id_for_broker" value={mls.hellosign_template_id_for_broker || ""} onChange={this.handleMlsFieldChange} />
                </div>

                <div className="form-section">
                  <label>Onboarding Instructions</label>
                  <p>Process to follow when onboarding for this MLS.</p>
                  <textarea name="onboarding_instructions" value={mls.onboarding_instructions || ""} onChange={this.handleMlsFieldChange} placeholder="" />
                </div>

              </div>
            </div>
            <div className={section === 'compliance' ? 'container' : 'hidden'}>
              <div className="panel half padded">
                <h3>Compliance Information</h3>
                <h3>General Compliance Info</h3>

                <div className="form-section">
                  <label>Logo</label>
                  <p className="muted">Must be either a .jpg or .png file.</p>
                  <Dropzone
                    className="logo-dropzone"
                    multiple={false}
                    accept="image/*"
                    maxSize={512000}
                    onDrop={this.handleFileDrop}>
                    {({ isDragActive, isDragReject }) => {
                      if (isDragActive) {
                        return this.getFilePreview('active')
                      }
                      if (isDragReject) {
                        return this.getFilePreview('reject')
                      }
                      return this.getFilePreview('inactive')
                    }}
                  </Dropzone>
                  {mls.mls_compliance.logo_url && <div className="current-logo">
                    <h3>Current Logo</h3>
                    <img src={mls.mls_compliance.logo_url}/>
                  </div>}
                </div>

                <div className="form-section">
                  <input id="mls_compliance.listtrac" name="mls_compliance.listtrac" type="checkbox" checked={mls.mls_compliance.listtrac || false} value="false" onChange={this.handleMlsFieldChange} />
                  <label htmlFor="mls_compliance.listtrac">Enable ListTrac for this MLS?</label>
                </div>

                <div className="form-section">
                  <input id="mls_compliance.price_per_sq_ft" name="mls_compliance.price_per_sq_ft" type="checkbox" checked={mls.mls_compliance.price_per_sq_ft || false} value="true" onChange={this.handleMlsFieldChange} />
                  <label htmlFor="mls_compliance.price_per_sq_ft">Display Price Per Square Foot?</label>
                </div>

                <div className="form-section">
                  <label>Courtesy Of Template: Commonly "Listing courtesy of __LISTING_AGENT__ of __LISTING_OFFICE__" or similar.</label>
                  <textarea name="mls_compliance.courtesy_of_template" value={mls.mls_compliance.courtesy_of_template || ""} onChange={this.handleMlsFieldChange} placeholder="none" />
                </div>

                <div className="form-section">
                  <label>Enter the text of the end-user agreement for this MLS. The user will see this in a pop-up and will need to accept the terms of the agreement before proceeding.</label>
                  <textarea name="mls_compliance.clickwrap_agreement_template" value={mls.mls_compliance.clickwrap_agreement_template || ""} onChange={this.handleMlsFieldChange} placeholder="none" />
                </div>

                <div className="form-section">
                  <label>List any fields (in our index) that this MLS does not allow. All fields will be treated as empty regardless of mapper. Space/newline separated.</label>
                  <textarea name="mls_compliance.forbidden_fields" value={mls.mls_compliance.forbidden_fields || ""} onChange={this.handleMlsFieldChange} placeholder="none" />
                </div>

                <h3>List View (aka Search Results)</h3>
                <div className="form-section">
                  <label>Logo URL to use on the Listing Preview: upload it to images.showcaseidx.com and enter here, or use %{`{`}logo_url{`}`}</label>
                  <input name="mls_compliance.list_view_listing_preview_logo_url" value={mls.mls_compliance.list_view_listing_preview_logo_url || ""} onChange={this.handleMlsFieldChange} placeholder="none" />
                </div>

                <div className="form-section">
                  <label>Small of disclaimer text to use on the listing preview area of the list view. Commonly a "courtesy of" thing.</label>
                  <textarea name="mls_compliance.list_view_listing_preview_disclaimer_template" value={mls.mls_compliance.list_view_listing_preview_disclaimer_template || ""} onChange={this.handleMlsFieldChange} placeholder="none" />
                </div>

                <div className="form-section">
                  <input id="mls_compliance.list_view_listing_preview_sqft_show_source" name="mls_compliance.list_view_listing_preview_sqft_show_source" type="checkbox" checked={mls.mls_compliance.list_view_listing_preview_sqft_show_source || ''} value="true" onChange={this.handleMlsFieldChange} />
                  <label htmlFor="mls_compliance.list_view_listing_preview_sqft_show_source">Should we show the SqFt Source in the list view listing preview?</label>
                </div>

                <div className="form-section">
                  <label>Main disclaimer at the bottom of a list view page. Tends to be large and use several variables.</label>
                  <textarea name="mls_compliance.list_view_disclaimer_template" value={mls.mls_compliance.list_view_disclaimer_template || ""} onChange={this.handleMlsFieldChange} placeholder="none" />
                </div>

                <div className="form-section">
                  <label>Max # of results allowed per search.</label><br />
                    <select name="mls_compliance.list_view_max_allowable_to_display" value={mls.mls_compliance.list_view_max_allowable_to_display || "500"} onChange={this.handleMlsFieldChange} >
                        <option name="100">100</option>
                        <option name="200">200</option>
                        <option name="300">300</option>
                        <option name="400">400</option>
                        <option name="500">500</option>
                    </select>
                </div>

                <h3>Listing Detail Page</h3>
                <div className="form-section">
                  <label>Small disclaimer used above the fold near key listing details.</label>
                  <input name="mls_compliance.detail_view_key_listing_data_disclaimer_template" value={mls.mls_compliance.detail_view_key_listing_data_disclaimer_template || ""} onChange={this.handleMlsFieldChange} placeholder="none" />
                </div>

                <div className="form-section">
                  <label>Main disclaimer at the bottom of a detail view page. Tends to be large and use several variables.</label>
                  <textarea name="mls_compliance.detail_view_bottom_disclaimer_template" value={mls.mls_compliance.detail_view_bottom_disclaimer_template || ""} onChange={this.handleMlsFieldChange} placeholder="none" />
                </div>

                <h3>Sold Data Configurations</h3>
                <div className="form-section">
                  <label>Sold Data Attribution: Commonly "Bought with __SELLING_AGENT__ of __SELLING_OFFICE__" or similar.</label>
                  <input name="mls_compliance.selling_agent_template" value={mls.mls_compliance.selling_agent_template || ""} onChange={this.handleMlsFieldChange} placeholder="none" />
                </div>

                <div className="form-section">
                  <label>Small of disclaimer text to use on the listing preview area of the list view. Usually Sold Data Attribution template.</label>
                  <input name="mls_compliance.sold_list_view_listing_preview_disclaimer_template" value={mls.mls_compliance.sold_list_view_listing_preview_disclaimer_template || ""} onChange={this.handleMlsFieldChange} placeholder="none" />
                </div>

                <div className="form-section">
                  <label>Small disclaimer used above the fold near key listing details. Usually Sold Data Attribution template.</label>
                  <input name="mls_compliance.sold_detail_view_key_listing_data_disclaimer_template" value={mls.mls_compliance.sold_detail_view_key_listing_data_disclaimer_template || ""} onChange={this.handleMlsFieldChange} placeholder="none" />
                </div>

                <div className="form-section">
                  <label>Main disclaimer at the bottom of a detail view page. Tends to be large and use several variables. Will only display on listings in sold status and overrides the original disclaimers. The only difference here should be the addition of {'%{selling_agent_template}'} below the {'%{courtesy_of_template}'}</label>
                  <textarea name="mls_compliance.sold_detail_view_bottom_disclaimer_template" value={mls.mls_compliance.sold_detail_view_bottom_disclaimer_template || ""} onChange={this.handleMlsFieldChange} placeholder="none" />
                </div>

                <h3>Provenance Disclaimers</h3>
                <p>Disclaimers shown near where a data item is displayed that explain where the data originated. Usually of the form "provided by ShowcaseIDX".
                Remove all disclaimers to prevent showing the disclaimer blocks.</p>

                {_.map(_.keys(disclaimers_text), (v, k) =>
                  <div key={k} className="form-section">
                    <label>Disclaimer for: {disclaimers_text[v] || ""}</label>
                    <input name={`mls_compliance.disclaimers.${v}`} value={disclaimers[v] || ""} onChange={this.handleMlsFieldChange} />
                  </div>
                )}

                <h3>Variables Available</h3>
                <p>The following are generated (back-end) on a per-website level (not per listing):</p>
                <ul>
                  {_.map(complianceBackEndVariables, (v,k) => {
                    return <li key={k}><em>%{`{` + k + `}`}</em>: {v}</li>
                  })}
                </ul>

                <p>The following are generated (front-end) on a per-listing level:</p>
                <ul>
                  {_.map(complianceFrontEndVariables, (v,k) => {
                    return <li key={k}><em>{k}</em>: {v}</li>
                  })}
                </ul>
              </div>
              <div className="panel half padded">
                <h3>Compliance Preview</h3>
                <div className="compliance_preview" style={{ border: "1px solid black", padding: "3px" }}>
                  <h3>List View</h3>
                  <div className="compliance_preview_list_view_listing"  style={{ border: "1px solid grey", padding: "3px" }}>
                    <h4>List View Listing Preview</h4>
                    <img src={this.MlsAdminUIStore.compliance_preview.list_view_listing_preview_logo_url} />
                    <div dangerouslySetInnerHTML={{ __html: this.MlsAdminUIStore.compliance_preview.list_view_listing_preview_disclaimer_template }} />
                    {this.MlsAdminUIStore.compliance_preview.list_view_listing_preview_sqft_show_source && (<div>SQFT SOURCE DISPLAYED HERE</div>)}
                  </div>
                  <div className="compliance_preview_list_view_disclaimer" dangerouslySetInnerHTML={{ __html: this.MlsAdminUIStore.compliance_preview.list_view_disclaimer_template }} />
                </div>
                <br />
                <div style={{ border: "1px solid black", padding: "3px" }}>
                  <h3>Listing Detail</h3>
                  <div style={{ border: "1px solid grey", padding: "3px" }}>
                    <h4>Listing Detail Key Summary</h4>
                    <div dangerouslySetInnerHTML={{ __html: this.MlsAdminUIStore.compliance_preview.detail_view_key_listing_data_disclaimer_template }} />
                  </div>
                  <div dangerouslySetInnerHTML={{ __html: this.MlsAdminUIStore.compliance_preview.detail_view_bottom_disclaimer_template }} />
                </div>
              </div>
            </div>
            <div className={section === 'coverage' ? 'container' : 'hidden'}>
              <div className="panel full padded">
                <h3>Coverage Information</h3>

                <p>The <a href="https://showcaseidx.com/mls-coverage/" target="_blank">Mls Coverage</a> section of
                our site is based on the data that you're editing here and is cached in Wordpress.</p>
                <p>
                If you want to see your changes right away, you'll need
                to <a href="https://showcaseidx.com/wp-admin/admin.php?page=showcaseidx_mls_coverage" target="_blank">clear the WP cache</a>.
                NOTE: log into our WP before clicking that link, the admin redirect seems broken.
                </p>

                <div className="form-section">
                  <label>Country where this MLS is located</label>
                  <input name="mls_coverage.country" value={mls.mls_coverage.country || ""} onChange={this.handleMlsFieldChange} placeholder="" />
                </div>

                <div className="form-section">
                  <div className="label-row">
                    <label>States or provinces where this MLS is located</label>
                    <div className="button-selector-group">
                      <button type="button" onClick={this.addAdminArea} >Add a state/province: </button>
                      <input id="admin_area" />
                    </div>
                  </div>
                  <div className="content-row">
                    <div className="pillbox">
                      {_.map(mls.mls_coverage.administrative_area_level_1, (name, index) =>
                        <div key={index} className="pill">
                          <span className="pill-label">{name}</span>
                          <Icon icon="times" onClick={() => this.removeAdminArea(name)}/>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="label-row">
                    <label>Top cities in this MLS</label>
                    <div className="button-selector-group">
                      <button type="button" onClick={this.addTopCity} >Add a city: </button>
                      <input id="top_city" />
                    </div>
                  </div>
                  <div className="content-row">
                    <div className="pillbox">
                      {_.map(mls.mls_coverage.top_cities, (name, index) =>
                        <div key={index} className="pill">
                          <span className="pill-label">{name}</span>
                          <Icon icon="times" onClick={() => this.removeTopCity(name)}/>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <label>Number of listings</label>
                  <input name="mls_coverage.num_listings" type="number" value={mls.mls_coverage.num_listings || ""} onChange={this.handleMlsFieldChange} placeholder="" />
                </div>

                <div className="form-section">
                  <label>Number of agents</label>
                  <input name="mls_coverage.num_agents" type="number" value={mls.mls_coverage.num_agents || ""} onChange={this.handleMlsFieldChange} placeholder="" />
                </div>

                <div className="form-section">
                  <label>Number of offices</label>
                  <input name="mls_coverage.num_offices" type="number" value={mls.mls_coverage.num_offices || ""} onChange={this.handleMlsFieldChange} placeholder="" />
                </div>

                <div className="form-section">
                  <label>Custom title</label>
                  <p>Used by marketing to override what is presented as the <i>title</i> of the MLS (will use mls.name if left blank).</p>
                  <input name="mls_coverage.custom_title" value={mls.mls_coverage.custom_title || ""} onChange={this.handleMlsFieldChange} placeholder="" />
                </div>

                <div className="form-section">
                  <label>Custom description</label>
                  <p>Used by marketing to provide what is presented as the <i>description</i> of the MLS.</p>
                  <textarea name="mls_coverage.custom_description" value={mls.mls_coverage.custom_description || ""} onChange={this.handleMlsFieldChange} placeholder="" />
                </div>

                <div className="form-section">
                  <label>Summary blurb</label>
                  <p>HTML for the <i>summary</i> section of the MLS web page template.</p>
                  <textarea name="mls_coverage.summary_blurb_html" value={mls.mls_coverage.summary_blurb_html || ""} onChange={this.handleMlsFieldChange} placeholder="" />
                  {/*   temporarily reverted to text area so video links can be entered via raw HTML
                  <RichTextEditor value={this.state.summary_blurb} onChange={v => this.handleMlsFieldChangeRTE('summary_blurb', v)} placeholder="" />
                  */}
                </div>

                <div className="form-section">
                  <label>Tools blurb</label>
                  <p>HTML for the <i>tools</i> section of the MLS web page template.</p>
                  <RichTextEditor value={this.state.tools_blurb} onChange={v => this.handleMlsFieldChangeRTE('tools_blurb', v)} placeholder="" />
                </div>

                <div className="form-section">
                  <label>Long blurb</label>
                  <p>HTML for the <i>long</i> section of the MLS web page template.</p>
                  <RichTextEditor value={this.state.long_blurb} onChange={v => this.handleMlsFieldChangeRTE('long_blurb', v)} placeholder="" />
                </div>

                <div className="form-section">
                  <label>MLS fee annotation</label>
                  <p>HTML to further explain how the pass-thru fee structure and process works for this MLS.</p>
                  <RichTextEditor value={this.state.mls_fee_annotation} onChange={v => this.handleMlsFieldChangeRTE('mls_fee_annotation', v)} placeholder="" />
                </div>

                <div className="form-section">
                  <label>Slug</label>
                  <p>Slug for the WP plugin</p>
                  <input name="mls_coverage.slug" value={mls.mls_coverage.slug || ""} onChange={this.handleMlsFieldChange} placeholder="" />
                </div>

                <div className="form-section">
                  <div className="label-row">
                    <label>Realtor associations</label>
                    <div className="button-selector-group">
                      <RaAutocomplete raStore={this.realtorAssociations} onSelect={this.addRealtorAssoc} clearAfterSelect={true} />
                    </div>
                  </div>
                  <div className="content-row">
                    <div className="pillbox">
                      {_.map(mls.mls_coverage.realtor_associations, ({id, name}) =>
                        <div key={id} className="pill">
                          <span className="pill-label">{name}</span>
                          <Icon icon="times" onClick={() => this.removeRealtorAssoc(id)}/>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>
        </Form>)}
      </div>
    )
  }
}



// WEBPACK FOOTER //
// client/components/sections/Admin/Mls/index.js